
import React, { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { 
  Users, 
  LayoutDashboard, 
  Building, 
  FileText, 
  ShieldCheck,
  ListTodo,
  History,
  Megaphone,
  Share2,
  ChevronDown,
  ChevronRight,
  List
} from "lucide-react";
import { UserRole } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useShareLists } from "@/hooks/useShareLists";

interface NavLinksProps {
  onClick?: () => void;
  isMobile?: boolean;
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  to: string;
  adminOnly: boolean;
  iconColor: string;
  activeGradient: string;
  children?: NavItem[];
  isDynamic?: boolean;
  title?: string;
}

const NavLinks = ({ onClick, isMobile = false }: NavLinksProps) => {
  const { profile } = useAuth();
  const role = profile?.role as UserRole || 'member';
  const location = useLocation();
  const { shareId } = useParams<{ shareId?: string }>();
  const { data: shareLists } = useShareLists();
  
  // Build dynamic children for Leads group
  const sharedChildren: NavItem[] = [];
  
  // "All Shared Leads" item
  sharedChildren.push({
    icon: List,
    label: 'All Shared Leads',
    to: '/shared-with-me',
    adminOnly: false,
    iconColor: 'text-indigo-500',
    activeGradient: 'from-indigo-500/10 to-indigo-500/5',
  });
  
  // Individual share lists
  if (shareLists) {
    shareLists.forEach((share) => {
      sharedChildren.push({
        icon: Share2,
        label: share.name || 'Untitled Share',
        to: `/shared-with-me/${share.id}`,
        adminOnly: false,
        iconColor: 'text-indigo-400',
        activeGradient: 'from-indigo-400/10 to-indigo-400/5',
        title: share.name || 'Untitled Share',
      });
    });
  }

  // Auto-expand Leads group when on a child route
  const isSharedRoute = location.pathname.startsWith('/shared-with-me');
  const [leadsExpanded, setLeadsExpanded] = useState(isSharedRoute || location.pathname === '/app');

  const navItems: NavItem[] = [
    { 
      icon: Users, 
      label: 'Leads', 
      to: '/app', 
      adminOnly: false,
      iconColor: 'text-blue-500',
      activeGradient: 'from-blue-500/10 to-blue-500/5',
      children: sharedChildren
    },
    { 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      to: '/dashboard', 
      adminOnly: false,
      iconColor: 'text-purple-500',
      activeGradient: 'from-purple-500/10 to-purple-500/5'
    },
    { 
      icon: ListTodo, 
      label: 'Tasks', 
      to: '/tasks', 
      adminOnly: false,
      iconColor: 'text-green-500',
      activeGradient: 'from-green-500/10 to-green-500/5'
    },
    { 
      icon: Building, 
      label: 'Companies', 
      to: '/companies', 
      adminOnly: false,
      iconColor: 'text-orange-500',
      activeGradient: 'from-orange-500/10 to-orange-500/5'
    },
    { 
      icon: FileText, 
      label: 'Documentation', 
      to: '/documentation', 
      adminOnly: false,
      iconColor: 'text-cyan-500',
      activeGradient: 'from-cyan-500/10 to-cyan-500/5'
    },
    { 
      icon: History, 
      label: 'Mail Merges', 
      to: '/mail-merges', 
      adminOnly: false,
      iconColor: 'text-pink-500',
      activeGradient: 'from-pink-500/10 to-pink-500/5'
    },
    { 
      icon: Megaphone, 
      label: 'Campaign Planner', 
      to: '/campaign-planner', 
      adminOnly: false,
      iconColor: 'text-teal-500',
      activeGradient: 'from-teal-500/10 to-teal-500/5'
    },
    { 
      icon: ShieldCheck, 
      label: 'Admin', 
      to: '/admin', 
      adminOnly: true,
      iconColor: 'text-red-500',
      activeGradient: 'from-red-500/10 to-red-500/5'
    }
  ];

  const renderNavLink = (item: NavItem, isChild = false) => {
    const IconComponent = item.icon;
    const isActive = item.to === '/shared-with-me' 
      ? location.pathname === '/shared-with-me' && !shareId
      : location.pathname === item.to;
    const hasChildren = item.children && item.children.length > 0;

    if (isMobile) {
      return (
        <React.Fragment key={item.to}>
          <Link
            to={item.to}
            className={cn(
              "flex items-center gap-3 w-full p-3 rounded-md hover:bg-accent transition-colors",
              isChild && "pl-10"
            )}
            onClick={onClick}
            title={item.title}
          >
            <IconComponent className="h-5 w-5" />
            <span className={cn("text-base text-left w-full", isChild && "truncate")}>{item.label}</span>
          </Link>
          {hasChildren && item.children!.map(child => renderNavLink(child, true))}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={item.to}>
        <div className="relative flex items-center">
          <Link
            to={item.to}
            className={cn(
              buttonVariants({ variant: "ghost" }),
              "w-full justify-start h-11 group relative overflow-hidden transition-all duration-200",
              isChild ? "pl-9 pr-3 h-9" : "px-3",
              "hover:bg-gradient-to-r hover:from-primary/10 hover:to-primary/5",
              "hover:shadow-sm hover:border-l-2 hover:border-primary",
              isActive && [
                "bg-gradient-to-r",
                item.activeGradient,
                "border-l-2 border-primary",
                "shadow-sm"
              ]
            )}
            onClick={onClick}
            title={item.title}
          >
            <IconComponent 
              className={cn(
                "mr-3 transition-all duration-200 shrink-0",
                isChild ? "h-4 w-4" : "h-5 w-5",
                item.iconColor,
                "group-hover:scale-110"
              )} 
            />
            <span className={cn("font-medium", isChild ? "text-xs truncate" : "text-sm")}>{item.label}</span>
            
            {isActive && (
              <span className="absolute right-3 h-2 w-2 rounded-full bg-primary animate-pulse" />
            )}
          </Link>
          {hasChildren && (
            <button
              type="button"
              className="absolute right-1 p-1 rounded hover:bg-accent transition-colors z-10"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setLeadsExpanded(prev => !prev);
              }}
            >
              {leadsExpanded 
                ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              }
            </button>
          )}
        </div>
        {hasChildren && leadsExpanded && item.children!.map(child => renderNavLink(child, true))}
      </React.Fragment>
    );
  };
  
  return (
    <div className="flex flex-col gap-1 w-full">
      {navItems
        .filter(item => !item.adminOnly || role === 'admin')
        .map(item => renderNavLink(item))}
    </div>
  );
};

export default NavLinks;
